import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeEditorDialog, type DotstormingTheme } from '@dotstorming/theme-editor';
import { useCallback } from 'react';

import { wallsApi } from '@walls/services/wallsApi';
import {
  normalizeWallTheme,
  useWallStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { FeedbackDialog } from './FeedbackDialog';
import { ShareDialog } from './ShareDialog';

interface WorkspaceOverlaysProps {
  board: WallBoard;
  topicKey?: string | null;
}

export function WorkspaceOverlays({ board, topicKey }: WorkspaceOverlaysProps) {
  const activeOverlay = useWorkspaceUiStore((state) => state.activeOverlay);
  const setActiveOverlay = useWorkspaceUiStore((state) => state.setActiveOverlay);
  const setTheme = useWallStore((state) => state.setTheme);

  const close = () => setActiveOverlay(null);

  const loadTheme = useCallback(async () => {
    return normalizeWallTheme(await wallsApi.getWallTheme(board.id, topicKey));
  }, [board.id, topicKey]);

  const saveTheme = useCallback(
    async (theme: DotstormingTheme) => {
      const nextTheme = normalizeWallTheme(
        await wallsApi.updateWallTheme(board.id, theme, topicKey)
      );
      setTheme(nextTheme);
      return nextTheme;
    },
    [board.id, setTheme, topicKey]
  );

  if (activeOverlay === 'theme') {
    return <ThemeEditorDialog open onClose={close} loadTheme={loadTheme} saveTheme={saveTheme} />;
  }

  if (activeOverlay === 'share') {
    return <ShareDialog board={board} onClose={close} open topicKey={topicKey} />;
  }

  if (activeOverlay === 'feedback') {
    return <FeedbackDialog open onClose={close} onSendFeedback={wallsApi.sendFeedback} />;
  }

  return (
    <Dialog open={Boolean(activeOverlay)} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Workspace</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">
            This workspace dialog shell replaces the Backbone overlay host.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The specific dialog content can be migrated from the related Backbone modules as those
            modules are replaced.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
